import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap, cx } from '@atlaskit/css';
// Subpath import — `@atlaskit/flag`'s root barrel is on the prohibited-barrel ratcheting list.
import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { Box, Flex, Pressable } from '@atlaskit/primitives/compiled';
// Type-only subpath import: `OptionType` is erased at build, and `/types` is not the prohibited
// root barrel, so this is safe for both the ratchet and the gemini-vr bundler.
import { type OptionType } from '@atlaskit/select/types';
import { token } from '@atlaskit/tokens';

import { type RovoLogoProps } from '../../../../common/constants/icons';
import { type NounKeys } from '../../../../common/constants/nouns';
import { ProductKeys } from '../../../../common/constants/product-keys';
import { useProductConfigs } from '../../../../common/constants/products';
import {
	isForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../../../common/constants/schemas/3p-config';
import type { TriggerComponentProps } from '../../../../common/types';
import { isWebCrawlerFanoutEnabled } from '../../../../common/ui/filters/app-filter-sidebar/app-filters/utils';
// PopupSelect is consumed via the local select-filter wrapper rather than the `@atlaskit/select`
// root barrel: the root barrel is on the prohibited-barrel ratchet, and the `/popup-select` subpath
// resolves to `undefined` under gemini-vr's atlaspack bundler.
import { AppFilterPopupSelect } from '../../../../common/ui/filters/select-filter';
import { Control } from '../../../../common/ui/filters/select-filter/control';
import { MenuListWithoutClearFilterOptionLabel } from '../../../../common/ui/filters/select-filter/menu-list';
import { OAuthWarningWrapper } from '../../../../common/ui/oauth-warning-wrapper';
import { messages as oAuthMessages } from '../../../../common/ui/oauth-warning-wrapper/messages';
import { ProductOrNounIcon } from '../../../../common/ui/product-icon';
import {
	type AppOption,
	getWebCrawlerDatasources,
} from '../../../../common/utils/app-filter-options';
import { warmUpCodeSearchForHoveredProduct } from '../../../../common/utils/code-search-warmup';
import { isProductConnected, isProductSmartlink } from '../../../../common/utils/connectors';
import {
	type HorizontalAppFilterScrollDirection,
	onHorizontalAppFilterScroll,
} from '../../../../common/utils/events/horizontal-app-filter-scroll';
import { OAuthSource } from '../../../../common/utils/events/oauth-events';
import { onAppFilterShowMoreClicked } from '../../../../common/utils/events/on-app-filter-show-more-clicked';
import { onAppFilterOptionClicked } from '../../../../common/utils/events/product-option-clicked';
import {
	onSearchPageFiltersViewed,
	type PixelVisibleApp,
} from '../../../../common/utils/events/search-page-filters-viewed';
import {
	buildKnownAppKeySet,
	getVisibleAppAuthStatus,
	parseAppKeyFromTestId,
} from '../../../../common/utils/events/visible-apps-dom';
import { getWorkspaceNameFromProduct } from '../../../../common/utils/get-workspace-name-from-product';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../controllers/analytics';
import { useIsUserOAuthed, useOAuthHandlers } from '../../../../controllers/oauth';
import { useBootstrap } from '../../../../controllers/store/bootstrap';
import { useSelectedDatasource, useSelectedProduct } from '../../../../controllers/store/search';
import { getScrollAmount as getScrollAmountFromLayout } from './get-scroll-amount';
import { usePromotedApps } from './promoted-apps';
import { useLiteAppOptions } from './use-lite-app-options';
import { useLiteFilterSelect } from './use-lite-filter-select';

const arePixelVisibleAppArraysEqual = (a: PixelVisibleApp[], b: PixelVisibleApp[]): boolean =>
	a.length === b.length &&
	a.every(
		(value, index) =>
			value.appKey === b[index].appKey && value.isAuthenticated === b[index].isAuthenticated,
	);

export const getPixelVisibleApps = (
	scrollEl: HTMLElement,
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
): PixelVisibleApp[] => {
	const containerRect = scrollEl.getBoundingClientRect();
	const containerLeft = scrollEl.scrollLeft;
	const containerRight = containerLeft + scrollEl.clientWidth;
	// Identity is read from `data-testid` (the same `filter-<key>` contract the
	// non-lite rail uses, and the one pollinator tests depend on); auth is derived
	// from state via the shared `isProductConnected` helper. See `visible-apps-dom`.
	// Known-app keys are built from the static set + runtime `thirdPartyConfigs`,
	// matching the rail so a 3P key present in configs but not in the static list
	// isn't silently dropped from the Lite payload.
	const knownAppKeys = buildKnownAppKeySet(thirdPartyConfigs);
	const pills = Array.from(scrollEl.querySelectorAll<HTMLElement>('[data-testid]'));
	// Guard against a descendant element re-emitting an already-counted app key
	// (mirrors the rail reader); first ≥50%-visible pill per key wins.
	const seen = new Set<string>();

	return pills.reduce<PixelVisibleApp[]>((result, pill) => {
		const testId = pill.getAttribute('data-testid');
		const key = testId ? parseAppKeyFromTestId(testId, knownAppKeys) : undefined;
		const pillRect = pill.getBoundingClientRect();
		const pillWidth = pillRect.width;
		if (!key || pillWidth === 0 || seen.has(key)) {
			return result;
		}

		const pillLeft = pillRect.left - containerRect.left + scrollEl.scrollLeft;
		const pillRight = pillLeft + pillWidth;
		const overlapLeft = Math.max(pillLeft, containerLeft);
		const overlapRight = Math.min(pillRight, containerRight);
		const overlap = Math.max(0, overlapRight - overlapLeft);

		if (overlap / pillWidth < 0.5) {
			return result;
		}

		seen.add(key);
		return [
			...result,
			{ appKey: key, isAuthenticated: getVisibleAppAuthStatus(key, thirdPartyConfigs) },
		];
	}, []);
};

const styles = cssMap({
	pill: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.075'),
		paddingInline: token('space.150'),
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: 'transparent',
		cursor: 'pointer',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		color: token('color.text.subtle'),
		backgroundColor: token('color.background.neutral'),
		whiteSpace: 'nowrap',
		flexShrink: 0,
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
			color: token('color.text.subtle'),
		},
	},
	pillSelected: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		borderColor: token('color.border.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
			color: token('color.text.selected'),
		},
	},
	scrollContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		overflowX: 'auto',
		scrollbarWidth: 'none',
		scrollBehavior: 'smooth',
	},
	outerContainer: {
		display: 'flex',
		alignItems: 'center',
		position: 'relative',
	},
	chevronButton: {
		display: 'flex',
	},
	chevronContainer: {
		position: 'absolute',
		top: '0',
		bottom: '0',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 1,
		paddingInline: token('space.025'),
	},
	chevronLeft: {
		left: '0',
	},
	chevronRight: {
		right: '0',
	},
	iconContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '16px',
		height: '16px',
		overflow: 'hidden',
		flexShrink: 0,
	},
	/**
	 * Truncates long app labels with "…" so the visible bar stays compact and predictable.
	 * 100px lets short names ("Jira", "Confluence", "Loom") render in full while clipping
	 * long names like "Jira Service Management" → "Jira Service…" and
	 * "Jira Product Discovery" → "Jira Product…". Applied only to pills in the lite header
	 * bar — the "More apps" dialog has more room and shows the full label.
	 */
	pillLabel: {
		maxWidth: '100px',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		display: 'block',
	},
});

const LiteAppIcon = ({
	productOrNounKey,
	thirdPartyConfigs,
	size,
	isSelected = false,
}: {
	productOrNounKey: ProductKeys | NounKeys | undefined;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	size: RovoLogoProps['size'];
	isSelected?: boolean;
}): React.JSX.Element => {
	const forgeIcon =
		productOrNounKey && isForgeConnectorProductKey(productOrNounKey)
			? thirdPartyConfigs[productOrNounKey]?.icon
			: undefined;

	return forgeIcon ? (
		forgeIcon({ label: '', size })
	) : (
		<ProductOrNounIcon
			label=""
			size={size}
			productOrNounKey={productOrNounKey}
			isSelected={isSelected}
		/>
	);
};

const AppPill = ({
	option,
	onSelect,
	thirdPartyConfigs,
	isDisabled,
}: {
	option: AppOption;
	onSelect: (value: ProductKeys | NounKeys | undefined) => void;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	isDisabled?: boolean;
}) => {
	const { formatMessage } = useIntl();
	const label = option.isUnauthenticated
		? formatMessage({
				id: 'search_page.lite_app_filter_bar.connect',
				defaultMessage: 'Connect',
				description:
					'Label for an unauthenticated 3P app pill prompting the user to connect it. The adjacent product icon identifies the app.',
			})
		: option.label;
	const ariaLabel = option.isUnauthenticated
		? formatMessage(
				{
					id: 'search_page.lite_app_filter_bar.connect_app_aria',
					defaultMessage: 'Connect {app}',
					description:
						'Accessible label for an unauthenticated 3P app pill. Visible label is "Connect"; the app name is added here for screen readers.',
				},
				{ app: option.label },
			)
		: undefined;

	return (
		<Pressable
			xcss={cx(styles.pill, option.isSelected && styles.pillSelected)}
			testId={`filter-${String(option.value)}`}
			onClick={() => {
				if (!isDisabled) {
					onSelect(option.value);
				}
			}}
			// Pre-warms DevAI's permission caches when a code host is hovered.
			onMouseEnter={() => warmUpCodeSearchForHoveredProduct(String(option.value))}
			aria-pressed={option.isSelected}
			aria-current={option.isSelected}
			aria-label={ariaLabel}
		>
			<Flex alignItems="center" gap="space.075">
				<Box xcss={styles.iconContainer}>
					<LiteAppIcon
						size="xxsmall"
						productOrNounKey={option.value}
						isSelected={option.isSelected}
						thirdPartyConfigs={thirdPartyConfigs}
					/>
				</Box>
				<Box xcss={styles.pillLabel}>{label}</Box>
				{option.isUnauthenticated && (
					<LinkExternalIcon label="" size="small" color={token('color.icon.subtle')} />
				)}
			</Flex>
		</Pressable>
	);
};

const AllPill = ({ isSelected, onSelect }: { isSelected: boolean; onSelect: () => void }) => {
	const { formatMessage } = useIntl();
	return (
		<Pressable
			xcss={cx(styles.pill, isSelected && styles.pillSelected)}
			testId="filter-all"
			onClick={onSelect}
			aria-pressed={isSelected}
			aria-current={isSelected}
		>
			{formatMessage({
				id: 'search_page.lite_app_filter_bar.all',
				defaultMessage: 'All',
				description: 'Label for the All filter pill that shows results from all apps',
			})}
		</Pressable>
	);
};

/**
 * Horizontal app filter bar for Lite mode. Shows "All" pill + up to 9 product pills
 * in a single horizontally-scrollable row. Chevron buttons appear on hover to scroll
 * left/right. A "Show all apps" dropdown provides access to overflow apps.
 */
export const LiteAppFilterBar = (): React.JSX.Element => {
	const [{ thirdPartyConfigs }] = useBootstrap();
	const [selectedProduct] = useSelectedProduct();
	const isAllSelected = !selectedProduct;

	const { visibleOptions } = useLiteAppOptions();
	const handleSelect = useLiteFilterSelect();
	const { promotedWebCrawlerDatasource } = usePromotedApps();

	// Clicking the web crawler pill should (re)select the specific website it represents, not the
	// generic "web pages" aggregate — so route its selection through the remembered datasource.
	const handlePillSelect = useCallback(
		(value: ProductKeys | NounKeys | undefined, index: number) =>
			handleSelect(
				value,
				index,
				value === ProductKeys.AtlassianWebCrawler ? promotedWebCrawlerDatasource : undefined,
			),
		[handleSelect, promotedWebCrawlerDatasource],
	);

	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	// See note in useLiteFilterSelect — useCommonAndPrivateAttributes returns a new object on
	// every render, so we read the latest values from a ref to keep fireScrollEvent stable.
	const attributesRef = useRef({ commonAttributes, nonPrivacySafeAttributes });
	useEffect(() => {
		attributesRef.current = { commonAttributes, nonPrivacySafeAttributes };
	}, [commonAttributes, nonPrivacySafeAttributes]);

	const scrollRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const [isHovering, setIsHovering] = useState(false);

	const lastFiredVisibleAppsRef = useRef<PixelVisibleApp[] | null>(null);
	const scrollSettleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const fireSearchPageFiltersViewedEvent = useCallback(() => {
		const el = scrollRef.current;
		if (!el) {
			return;
		}

		const pixelVisibleApps = getPixelVisibleApps(el, thirdPartyConfigs);
		if (
			lastFiredVisibleAppsRef.current !== null &&
			arePixelVisibleAppArraysEqual(lastFiredVisibleAppsRef.current, pixelVisibleApps)
		) {
			return;
		}

		lastFiredVisibleAppsRef.current = pixelVisibleApps;
		fireAnalyticsEvent(
			onSearchPageFiltersViewed({
				commonAttributes: attributesRef.current.commonAttributes,
				nonPrivacySafeAttributes: attributesRef.current.nonPrivacySafeAttributes,
				visibleApps: pixelVisibleApps,
			}),
		);
	}, [fireAnalyticsEvent, thirdPartyConfigs]);

	const fireSearchPageFiltersViewedEventRef = useRef(fireSearchPageFiltersViewedEvent);
	useEffect(() => {
		fireSearchPageFiltersViewedEventRef.current = fireSearchPageFiltersViewedEvent;
	}, [fireSearchPageFiltersViewedEvent]);

	// Debounce the initial fire so apps in the right rail have time to stabilise
	// before we snapshot which ones are pixel-visible.
	const scheduleSearchPageFiltersViewedEvent = useCallback(() => {
		return setTimeout(() => fireSearchPageFiltersViewedEventRef.current(), 250);
	}, []);
	const scheduleSearchPageFiltersViewedEventRef = useRef(scheduleSearchPageFiltersViewedEvent);
	useEffect(() => {
		scheduleSearchPageFiltersViewedEventRef.current = scheduleSearchPageFiltersViewedEvent;
	}, [scheduleSearchPageFiltersViewedEvent]);

	const scheduleSearchPageFiltersViewedEventAfterScroll = useCallback(() => {
		if (scrollSettleTimeoutRef.current !== null) {
			clearTimeout(scrollSettleTimeoutRef.current);
		}
		scrollSettleTimeoutRef.current = setTimeout(() => {
			scrollSettleTimeoutRef.current = null;
			fireSearchPageFiltersViewedEventRef.current();
		}, 350);
	}, []);

	useEffect(() => {
		const timeoutId = scheduleSearchPageFiltersViewedEventRef.current();
		return () => {
			clearTimeout(timeoutId);
			if (scrollSettleTimeoutRef.current !== null) {
				clearTimeout(scrollSettleTimeoutRef.current);
			}
		};
	}, []);

	const fireScrollEvent = useCallback(
		(direction: HorizontalAppFilterScrollDirection) => {
			const el = scrollRef.current;
			if (!el) {
				return;
			}
			fireAnalyticsEvent(
				onHorizontalAppFilterScroll({
					commonAttributes: attributesRef.current.commonAttributes,
					nonPrivacySafeAttributes: attributesRef.current.nonPrivacySafeAttributes,
					direction,
				}),
			);
		},
		[fireAnalyticsEvent],
	);

	const updateScrollState = useCallback(() => {
		const el = scrollRef.current;
		if (!el) {
			return;
		}
		setCanScrollLeft(el.scrollLeft > 0);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
	}, []);

	useEffect(() => {
		const el = scrollRef.current;
		if (!el) {
			return;
		}
		const handleScroll = () => {
			updateScrollState();
			scheduleSearchPageFiltersViewedEventAfterScroll();
		};
		updateScrollState();
		const unbindScroll = bind(el, { type: 'scroll', listener: handleScroll });
		const resizeObserver = new ResizeObserver(updateScrollState);
		resizeObserver.observe(el);
		return () => {
			unbindScroll();
			resizeObserver.disconnect();
		};
	}, [scheduleSearchPageFiltersViewedEventAfterScroll, updateScrollState]);

	const computeScrollAmount = useCallback((direction: 'left' | 'right') => {
		const el = scrollRef.current;
		if (!el) {
			return 0;
		}

		const children = Array.from(el.children) as HTMLElement[];
		return getScrollAmountFromLayout(
			direction,
			{ scrollLeft: el.scrollLeft, clientWidth: el.clientWidth },
			children.map((child) => ({ offsetLeft: child.offsetLeft, offsetWidth: child.offsetWidth })),
		);
	}, []);

	const scrollLeft = useCallback(() => {
		const amount = computeScrollAmount('left');
		if (amount === 0) {
			return;
		}
		fireScrollEvent('left');
		scrollRef.current?.scrollBy({ left: -amount, behavior: 'smooth' });
	}, [computeScrollAmount, fireScrollEvent]);

	const scrollRight = useCallback(() => {
		const amount = computeScrollAmount('right');
		if (amount === 0) {
			return;
		}
		fireScrollEvent('right');
		scrollRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
	}, [computeScrollAmount, fireScrollEvent]);

	return (
		<Box
			xcss={styles.outerContainer}
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
		>
			{isHovering && canScrollLeft && (
				<Box
					xcss={cx(styles.chevronContainer, styles.chevronLeft)}
					style={{
						background: `linear-gradient(to right, ${token(
							'elevation.surface',
						)} 28px, transparent 55px)`,
						paddingInline: `${token('space.0')} ${token('space.400')}`,
					}}
				>
					<Box xcss={styles.chevronButton}>
						<IconButton
							appearance="default"
							spacing="default"
							label="Scroll left"
							icon={ChevronLeftIcon}
							onClick={scrollLeft}
						/>
					</Box>
				</Box>
			)}

			<Box ref={scrollRef} xcss={styles.scrollContainer} testId="lite-app-filter-scroll-container">
				<AllPill isSelected={isAllSelected} onSelect={() => handleSelect(undefined, 0)} />
				{visibleOptions.map((option, idx) => {
					// 1-based index so the "All" pill remains at position 0 — matches the
					// position semantics of `index` on the right-panel app filter event.
					const pillIndex = idx + 1;
					return option.isUnauthenticated ? (
						<OAuthWarningWrapper
							key={option.value}
							product={option.value as ProductKeys}
							isSmartlink={isProductSmartlink({
								productKey: option.value,
								thirdPartyConfigs,
							})}
							useOAuthHandlers={useOAuthHandlers}
							useIsUserOAuthed={useIsUserOAuthed}
							oAuthSource={OAuthSource.searchPageFilter}
							thirdPartyConfigs={thirdPartyConfigs}
							skipFlagsProvider={false}
							onAppFilterClick={() => {
								fireAnalyticsEvent(
									onAppFilterOptionClicked({
										commonAttributes: attributesRef.current.commonAttributes,
										nonPrivacySafeAttributes: attributesRef.current.nonPrivacySafeAttributes,
										productKey: option.value as ProductKeys,
										isConnected: isProductConnected(option.value, thirdPartyConfigs),
										index: pillIndex,
										isInShowMore: false,
									}),
								);
							}}
							trigger={({ isDisabled }: TriggerComponentProps) => (
								<AppPill
									option={option}
									onSelect={(value) => handlePillSelect(value, pillIndex)}
									thirdPartyConfigs={thirdPartyConfigs}
									isDisabled={isDisabled}
								/>
							)}
						/>
					) : (
						<AppPill
							key={option.value}
							option={option}
							onSelect={(value) => handlePillSelect(value, pillIndex)}
							thirdPartyConfigs={thirdPartyConfigs}
						/>
					);
				})}
			</Box>

			{isHovering && canScrollRight && (
				<Box
					xcss={cx(styles.chevronContainer, styles.chevronRight)}
					style={{
						background: `linear-gradient(to left, ${token(
							'elevation.surface',
						)} 28px, transparent 55px)`,
						paddingInline: `${token('space.400')} ${token('space.0')}`,
					}}
				>
					<Box xcss={styles.chevronButton}>
						<IconButton
							appearance="default"
							spacing="default"
							label="Scroll right"
							icon={ChevronRightIcon}
							onClick={scrollRight}
						/>
					</Box>
				</Box>
			)}
		</Box>
	);
};

type LiteAppSelectOption = {
	value: string;
	label: string;
	isSelected: boolean;
	isUnauthenticated: boolean;
	// Set for fanned-out Atlassian Web Crawler website rows: `value` is a per-website composite id
	// (for PopupSelect identity), so `productKey` carries the real filter key (for the icon +
	// selection) and `datasource` the specific website to scope the selection to.
	productKey?: ProductKeys | NounKeys;
	datasource?: ThirdPartyConfigsWithAPIResponse;
};

const allAppsStyles = cssMap({
	optionRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
		width: '100%',
	},
	optionMain: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		overflow: 'hidden',
	},
	optionLabel: {
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		font: token('font.body'),
		color: token('color.text'),
	},
	connectText: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		color: token('color.link'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		whiteSpace: 'nowrap',
		flexShrink: 0,
	},
});

const LiteShowAllAppsButtonImpl = (): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	// useCommonAndPrivateAttributes returns a new object each render; hold the latest in a ref so
	// the memoized handleMenuOpen below stays stable (mirrors useLiteFilterSelect).
	const attributesRef = useRef({ commonAttributes, nonPrivacySafeAttributes });
	useEffect(() => {
		attributesRef.current = { commonAttributes, nonPrivacySafeAttributes };
	}, [commonAttributes, nonPrivacySafeAttributes]);
	const [{ thirdPartyConfigs }] = useBootstrap();
	const products = useProductConfigs();

	const { visibleOptions, overflowOptions } = useLiteAppOptions();
	// `isInShowMore` stamps the option-clicked analytics as coming from the dialog,
	// preserving the event shape the previous "All apps" popup fired.
	const handleSelect = useLiteFilterSelect({ isInShowMore: true });
	const { promoteApp } = usePromotedApps();
	const [selectedProduct] = useSelectedProduct();
	const [selectedDatasource] = useSelectedDatasource();

	const appOptions = useMemo(
		() => [...visibleOptions, ...overflowOptions],
		[visibleOptions, overflowOptions],
	);

	// Atlassian Web Crawler website datasources to fan out into individual dialog rows.
	// Gated (via the shared `isWebCrawlerFanoutEnabled`) behind the same
	// `webcrawler-fanout-ui-sidebar-enabled` feature gate the standard right rail uses, so the
	// Lite "More apps" dialog lists individual websites like the standard right rail does.
	const webCrawlerDatasources = useMemo(
		() =>
			isWebCrawlerFanoutEnabled(thirdPartyConfigs)
				? getWebCrawlerDatasources(thirdPartyConfigs)
				: [],
		[thirdPartyConfigs],
	);

	const selectOptions = useMemo<LiteAppSelectOption[]>(() => {
		const otherOptions: LiteAppSelectOption[] = [];
		const webCrawlerWebsites: LiteAppSelectOption[] = [];

		for (const option of appOptions) {
			// Fan the Atlassian Web Crawler out into one option per website datasource (e.g.
			// "Atlassian Developer") so the dialog lists individual websites, matching the standard
			// right rail. Each row gets a unique `value` for PopupSelect identity and carries its
			// `datasource`/`productKey` so selection + the icon resolve correctly.
			if (option.value === ProductKeys.AtlassianWebCrawler && webCrawlerDatasources.length > 0) {
				for (const datasource of webCrawlerDatasources) {
					webCrawlerWebsites.push({
						value: `${option.value}-${datasource.datasourceId}`,
						label: datasource.workspaceName || datasource.workspaceUrl || option.label,
						isSelected:
							selectedProduct === ProductKeys.AtlassianWebCrawler &&
							selectedDatasource?.datasourceId === datasource.datasourceId,
						isUnauthenticated: Boolean(option.isUnauthenticated),
						productKey: option.value,
						datasource,
					});
				}
			} else {
				otherOptions.push({
					value: String(option.value),
					label: option.label,
					isSelected: option.isSelected,
					isUnauthenticated: Boolean(option.isUnauthenticated),
				});
			}
		}

		// Selecting one website promotes the single web crawler entry to the front, which would
		// otherwise drag every fanned-out website to the top. Instead surface only the selected
		// website at the top and keep the rest grouped in the web crawler's natural position
		// (further down the list), so only the chosen website "propagates" up.
		const selectedWebsiteIndex = webCrawlerWebsites.findIndex((option) => option.isSelected);
		if (selectedWebsiteIndex === -1) {
			return [...otherOptions, ...webCrawlerWebsites];
		}
		const [selectedWebsite] = webCrawlerWebsites.splice(selectedWebsiteIndex, 1);
		return [selectedWebsite, ...otherOptions, ...webCrawlerWebsites];
	}, [appOptions, webCrawlerDatasources, selectedProduct, selectedDatasource]);

	const hasSelectedOverflow = overflowOptions.some((option) => option.isSelected);

	const [connectProduct, setConnectProduct] = useState<ProductKeys | undefined>(undefined);
	const { outboundAuthUrl } = useIsUserOAuthed(connectProduct);
	const connectProductName = connectProduct
		? getWorkspaceNameFromProduct(connectProduct, thirdPartyConfigs) ||
			String(products[connectProduct]?.displayName ?? '')
		: '';
	const { onAuthorize } = useOAuthHandlers({
		product: connectProduct,
		successFlagTitle: formatMessage(oAuthMessages.authorizationSuccess, {
			product: connectProductName,
		}),
		failFlagTitle: formatMessage(oAuthMessages.authorizationFail, { product: connectProductName }),
		outboundAuthUrl: outboundAuthUrl || '',
	});
	// `onAuthorize` is a fresh reference each render; keep the latest in a ref (assigned in an
	// effect to keep render pure) so the connect effect below only re-runs when the
	// selected-to-connect app or its resolved auth URL changes. This effect is declared first so
	// the ref is updated before the connect effect reads it on the same commit.
	const onAuthorizeRef = useRef(onAuthorize);
	useEffect(() => {
		onAuthorizeRef.current = onAuthorize;
	}, [onAuthorize]);

	useEffect(() => {
		if (connectProduct && outboundAuthUrl) {
			onAuthorizeRef.current(OAuthSource.searchPageFilter);
			setConnectProduct(undefined);
		}
	}, [connectProduct, outboundAuthUrl]);

	const formatOptionLabel = useCallback(
		(option: OptionType) => {
			const opt = option as LiteAppSelectOption;
			// For fanned-out web-crawler rows the `value` is a composite id, so resolve the icon
			// from the real `productKey` when present.
			const optionValue = (opt.productKey ?? opt.value) as ProductKeys | NounKeys | undefined;
			return (
				// Pre-warms DevAI's permission caches when a code host is hovered.
				<Box
					xcss={allAppsStyles.optionRow}
					onMouseEnter={() => warmUpCodeSearchForHoveredProduct(String(optionValue))}
				>
					<Box xcss={allAppsStyles.optionMain}>
						<LiteAppIcon
							size="small"
							productOrNounKey={optionValue}
							thirdPartyConfigs={thirdPartyConfigs}
						/>
						<Box xcss={allAppsStyles.optionLabel}>{opt.label}</Box>
					</Box>
					{opt.isUnauthenticated && (
						<Box xcss={allAppsStyles.connectText}>
							{formatMessage({
								id: 'search_page.lite_app_filter_bar.connect',
								defaultMessage: 'Connect',
								description:
									'Label next to an unconnected third-party app in the More apps dropdown; selecting it starts the connect flow.',
							})}
						</Box>
					)}
				</Box>
			);
		},
		[formatMessage, thirdPartyConfigs],
	);

	const onChange = useCallback(
		(value: OptionType | readonly OptionType[] | null) => {
			const selected = (Array.isArray(value) ? value[0] : value) as LiteAppSelectOption | null;
			if (!selected) {
				return;
			}
			// For fanned-out web-crawler websites, `value` is a composite id — the real filter key
			// is `productKey`, and `datasource` scopes the selection to that specific website.
			const productKey = (selected.productKey ?? selected.value) as ProductKeys | NounKeys;
			// Unconnected 3P app: start the OAuth connect flow instead of applying a filter.
			if (selected.isUnauthenticated) {
				setConnectProduct(productKey as ProductKeys);
				return;
			}
			const index = appOptions.findIndex((option) => String(option.value) === String(productKey));
			// Promote the app so it renders as a pill at the start of the visible bar,
			// matching the previous "All apps" dialog behaviour.
			promoteApp(String(productKey));
			handleSelect(productKey, index === -1 ? undefined : index, selected.datasource);
		},
		[appOptions, promoteApp, handleSelect],
	);

	const handleMenuOpen = useCallback(() => {
		fireAnalyticsEvent(
			onAppFilterShowMoreClicked({
				commonAttributes: attributesRef.current.commonAttributes,
				nonPrivacySafeAttributes: attributesRef.current.nonPrivacySafeAttributes,
				isConnected: true,
				section: 'filterResults',
			}),
		);
	}, [fireAnalyticsEvent]);

	const isOptionSelected = useCallback((option: LiteAppSelectOption) => option.isSelected, []);

	if (overflowOptions.length === 0) {
		return null;
	}

	const moreAppsLabel = formatMessage({
		id: 'search_page.lite_app_filter_bar.more_apps',
		defaultMessage: 'More apps',
		description: 'Label for the dropdown that shows the full list of app filters',
	});

	return (
		<AppFilterPopupSelect<LiteAppSelectOption>
			label={moreAppsLabel}
			placeholder={formatMessage({
				id: 'search_page.lite_app_filter_bar.find_apps',
				defaultMessage: 'Find apps',
				description: 'Placeholder for the search input in the More apps filter dropdown',
			})}
			options={selectOptions}
			isOptionSelected={isOptionSelected}
			onChange={onChange}
			onMenuOpen={handleMenuOpen}
			formatOptionLabel={formatOptionLabel}
			isSearchable
			searchThreshold={-1}
			blurInputOnSelect={false}
			isClearable={false}
			closeMenuOnSelect
			shouldCloseMenuOnTab={false}
			// Match the previous dialog's footprint: ~259px wide, and tall enough to show the
			// 6 connected + 3 unauthenticated apps (max 9); any remaining apps scroll.
			minMenuWidth={259}
			maxMenuWidth="259px"
			maxMenuHeight={336}
			menuPlacement="bottom"
			components={{ Control, MenuList: MenuListWithoutClearFilterOptionLabel }}
			styles={{
				option: (baseStyles) => ({ ...baseStyles, cursor: 'pointer' }),
			}}
			popperProps={{
				strategy: 'fixed',
				modifiers: [
					{ name: 'offset', options: { offset: [0, 8] } },
					{ name: 'preventOverflow', enabled: false },
				],
			}}
			// The trigger stays the bespoke "More apps" button (no visual change); the
			// selected app is surfaced as a promoted pill in the bar and marked in the menu
			// via each option's `isSelected`.
			target={({ isOpen, ...targetProps }) => (
				<Button
					{...targetProps}
					appearance="default"
					isSelected={hasSelectedOverflow || isOpen}
					iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} size="small" />}
				>
					{moreAppsLabel}
				</Button>
			)}
		/>
	);
};

// `FlagsProvider` is required by `useOAuthHandlers` (it calls `useFlags`, which throws without a
// provider) used for the 3P connect flow. `OAuthWarningWrapper` wraps itself the same way.
export const LiteShowAllAppsButton = (): React.JSX.Element => (
	<FlagsProvider>
		<LiteShowAllAppsButtonImpl />
	</FlagsProvider>
);
